import { getFlattenOptions } from 'src/@vymo/ui/atoms/select/queries';
import { Option } from 'src/@vymo/ui/atoms/select/types';
import Quill from 'quill';
import imageCompression from 'browser-image-compression';
import {
  MAX_FILE_SIZE_MB,
  SPACE_CHAR,
  TRIGGER_BOX_CLOSE_KEYS,
  TRIGGER_BOX_VERTICAL_OFFSET,
  TRIGGER_FIELDS,
} from './constants';
import {
  CustomModules,
  HeaderButton,
  QuillToolbarConfig,
  QuillToolbarHandlers,
  RichTextEditorOption,
} from './types';

const Inline = Quill.import('blots/inline');

export const getAllMappedEditorOptions = (
  mappedMenuOptions: RichTextEditorOption[],
  allTriggerOptions: RichTextEditorOption[],
) => [...mappedMenuOptions, ...allTriggerOptions];

export const getEditorOptionTypes = (
  editorOptions: RichTextEditorOption[],
): string[] =>
  editorOptions
    .map(({ editorProps }) => editorProps?.type)
    .filter((type): type is string => typeof type === 'string');

export const getFilteredTriggerOptions = (
  mappedTriggerOptions: RichTextEditorOption[],
  triggerQuery: string,
) => {
  const normalizedQuery = triggerQuery.toLowerCase();
  if (!normalizedQuery) return mappedTriggerOptions;

  return mappedTriggerOptions.filter((option) => {
    const optionLabel = String(option.name ?? option.label ?? '');
    const renderedLabel = `${option.displayPrefix ?? ''}${optionLabel}`;

    return [optionLabel, renderedLabel].some((label) =>
      label.toLowerCase().startsWith(normalizedQuery),
    );
  });
};

export const getTriggerBoxPosition = (bounds: {
  top: number;
  left: number;
  height: number;
}) => ({
  top: bounds.top + bounds.height + TRIGGER_BOX_VERTICAL_OFFSET,
  left: bounds.left,
});

export const shouldCloseTriggerBox = (key: string) =>
  /\s/.test(key) || TRIGGER_BOX_CLOSE_KEYS.includes(key);

export const shouldAppendTriggerQuery = ({
  key,
  ctrlKey,
  metaKey,
  altKey,
}: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>) =>
  key.length === 1 && !ctrlKey && !metaKey && !altKey;

export const getHeaderNode = ({
  type,
  actionData,
  color,
}: HeaderButton) => `<span data-richEditorOptionType=${type} style='
        color: ${color}'
      >${actionData}</span><span>${SPACE_CHAR}</span>`;

export const getOptionNodeDetails = (
  option: RichTextEditorOption,
  legacy: boolean,
) => {
  const {
    editorProps,
    color,
    displayPrefix,
    code,
    name,
    label,
    value: optionValue,
  } = option;
  const displayContent = displayPrefix
    ? `${displayPrefix}${name ?? label}`
    : `${name ?? label}`;
  const optionNode = `<span data-richEditorOptionType=${
    editorProps?.type
  } style='
        color: ${color}'
        data-richEditorOptionDisplayPrefix='${displayPrefix}'
        data-richEditorOptionCode=${code ?? optionValue}
      >${legacy ? '' : '@'}${displayContent}</span><span>${SPACE_CHAR}</span>`;

  return { displayContent, optionNode };
};

export const getOptionInsertionIndex = ({
  index,
  fromHeader,
  triggerStartIndex,
  triggerQuery,
}: {
  index: number;
  fromHeader?: boolean;
  triggerStartIndex: number | null;
  triggerQuery: string;
}) =>
  fromHeader
    ? index
    : triggerStartIndex ?? Math.max(0, index - triggerQuery.length - 1);

export const getOptionSelectionIndex = ({
  insertionIndex,
  fromHeader,
  legacy,
  optionNode,
  displayContent,
}: {
  insertionIndex: number;
  fromHeader?: boolean;
  legacy: boolean;
  optionNode: string;
  displayContent: string;
}) => {
  const insertedContentLength = `${
    legacy || fromHeader ? '' : '@'
  }${displayContent}${SPACE_CHAR}`.length;

  return (
    insertionIndex +
    (fromHeader ? optionNode.length + 1 : insertedContentLength)
  );
};

export class RichEditorOptionBlot extends Inline {
  static blotName = 'richEditorOption';

  static tagName = 'span';

  static create(value: any) {
    const node = super.create();
    if (value && value.type) {
      node.setAttribute('data-richEditorOptionType', value.type);
    }
    if (value && value.color) {
      node.setAttribute('style', `color: ${value.color}`);
    }
    if (value && value.displayPrefix) {
      node.setAttribute(
        'data-richEditorOptionDisplayPrefix',
        value.displayPrefix,
      );
    }
    if (value && value.code) {
      node.setAttribute('data-richEditorOptionCode', value.code);
    }
    return node;
  }

  static formats(node: HTMLElement) {
    return {
      type: node.getAttribute('data-richEditorOptionType'),
      color: node.style.color,
      displayPrefix: node.getAttribute('data-richEditorOptionDisplayPrefix'),
      code: node.getAttribute('data-richEditorOptionCode'),
    };
  }
}

export const compressImageToBase64 = async (file: File): Promise<string> => {
  try {
    const options = {
      maxSizeMB: MAX_FILE_SIZE_MB,
      maxWidthOrHeight: 1000,
      useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    const base64 = await imageCompression.getDataUrlFromFile(compressedFile);
    return base64;
  } catch (error) {
    throw new Error(`Failed to compress image: ${(error as Error).message}`);
  }
};

export function MaxLength(quill, options) {
  quill.on('text-change', (delta, oldDelta, source) => {
    if (source === 'user') {
      const text = quill.getText();
      if (text.length - 1 > options.maxLength) {
        quill.deleteText(options.maxLength, text.length);
      }
    }
  });
}

export const removeSpaceFromText = (
  value: string,
  removeWhiteSpace: boolean = true,
) =>
  removeWhiteSpace
    ? value.replace(/[\u200B-\u200D\uFEFF\u00a0]| /g, '')
    : value.replace(/[\u200B-\u200D\uFEFF]/g, '');

const getOptionReplacement = (
  childElement: HTMLElement,
  options: Array<Option>,
  legacy: boolean,
): string => {
  const nodeOptionCode = childElement.getAttribute('data-richEditorOptionCode');
  if (!nodeOptionCode) return '';

  const matched = options.find((option) => {
    const { code, value } = option;
    return (code ?? value) === nodeOptionCode;
  });

  if (!matched) return '';

  const { displayPrefix = '', code, value, name, label } = matched;
  const target = `${legacy ? '' : '@'}${displayPrefix}${String(name ?? label)}`;
  return (
    childElement?.textContent?.replace(target, String(code ?? value)) ?? ''
  );
};

export const getFlattenedTriggerOptions = (input: any[]): Option[] =>
  getFlattenOptions(input).map((option) => ({
    ...option,
    editorProps: { type: TRIGGER_FIELDS },
  }));

export const getUpdatedCodeValue = (
  childNodes: NodeList,
  options: Array<Option>,
  legacy: boolean,
  valueObject: { value: string } = { value: '' },
): string => {
  childNodes.forEach((element) => {
    if (element.nodeName === '#text') {
      valueObject.value += element.nodeValue;
      return;
    }

    const isCustomOption = (element as HTMLElement)?.getAttribute(
      'data-richEditorOptionCode',
    );
    if (isCustomOption) {
      valueObject.value += getOptionReplacement(
        element as HTMLElement,
        options,
        legacy,
      );
      return;
    }

    if (element.childNodes.length > 0) {
      getUpdatedCodeValue(element.childNodes, options, legacy, valueObject);
    } else {
      valueObject.value += element.textContent;
    }
  });

  return valueObject.value;
};

export const getOptionsSelectedFromQuill = (
  editorOptionTypes: Array<string>,
  isLegacy: boolean,
  flattenedEditorOptionsForList: Array<any>,
  editorRootElement: HTMLElement,
) => {
  const optionsSelected: Record<string, any[]> = {};

  editorOptionTypes.forEach((type) => {
    const nodes = Array.from(
      editorRootElement.querySelectorAll(
        `[data-richEditorOptionType="${type}"]`,
      ),
    );

    const selectedCodes = nodes
      .map((node) =>
        (node as HTMLElement).getAttribute('data-richEditorOptionCode')?.trim(),
      )
      .filter(Boolean);

    const uniqueCodes = Array.from(new Set(selectedCodes));

    if (isLegacy) {
      optionsSelected[type] = uniqueCodes;
    } else {
      const filteredOptions = flattenedEditorOptionsForList.filter(
        (opt) => opt?.editorProps?.type === type,
      );

      optionsSelected[type] = uniqueCodes
        .map((code) =>
          filteredOptions.find(
            (option) =>
              (option.value === code || option.code === code) &&
              option.editorProps?.type === type,
          ),
        )
        .filter(Boolean);
    }
  });

  return optionsSelected;
};

export const getTextAreaValue = (
  childNodes: NodeList,
  removeSpace: boolean,
  removeWhiteSpace: boolean,
  options: Array<Option>,
  legacy: boolean,
) => {
  const updatedValue = getUpdatedCodeValue(childNodes, options, legacy);
  if (removeSpace) {
    return removeSpaceFromText(updatedValue, removeWhiteSpace);
  }
  return updatedValue;
};

export const getQuillModules = ({
  maxLength,
  showToolbar,
  toolbarConfig,
  toolbarHandlers,
  disableTextFormatting,
  singleLineInput,
  getEditor,
  viewMode,
}: {
  maxLength?: number;
  showToolbar?: boolean;
  toolbarConfig?: QuillToolbarConfig;
  toolbarHandlers?: QuillToolbarHandlers;
  disableTextFormatting?: boolean;
  singleLineInput?: boolean;
  getEditor: () => Quill | undefined;
  viewMode?: boolean;
}) => {
  const shouldShowToolbar = showToolbar !== false && viewMode === false;
  const baseModules: CustomModules = {
    toolbar: shouldShowToolbar
      ? { container: toolbarConfig, handlers: toolbarHandlers }
      : false,
    maxLength: maxLength ? { maxLength } : undefined,
    keyboard: {
      bindings: {
        customEnter: {
          key: 'Enter',
          handler: (range) => {
            if (singleLineInput) return false;
            const quill = getEditor();
            if (quill) {
              quill.insertText(range.index, '\n', 'user');
              quill.setSelection(range.index + 1, 'silent');
            }
            return true;
          },
        },
      },
    },
    imageDropAndPaste: {
      handler: async (dataUrl: string, type: string, imageData: File) => {
        const base64 = await compressImageToBase64(imageData);
        return base64;
      },
    },
    'emoji-toolbar': true,
  };

  if (disableTextFormatting) {
    baseModules.clipboard = {
      matchers: [
        [
          '*',
          (node, delta) => {
            delta.ops.forEach((op) => {
              if (op.attributes) delete op.attributes;
            });
            return delta;
          },
        ],
      ],
    };
  }

  return baseModules;
};
